import { useState } from "react";

const checklist = [
  { item: "Natural linen or linen-cotton blend", note: "Avoid synthetic fabrics — they look flat in natural light" },
  { item: "Warm tone — ivory, oat, or natural linen", note: "Avoid stark white or cool grey" },
  { item: "Floor-to-ceiling length", note: "Hang the rod as close to the ceiling as possible" },
  { item: "Generous fullness", note: "1.5x to 2x the window width for a relaxed, gathered look" },
  { item: "Correct header style", note: "Eyelet for relaxed; pinch pleat for tailored" },
  { item: "Appropriate lining", note: "Unlined for sheer; thermal lining for light control" },
  { item: "Warm metal hardware", note: "Brushed brass or warm bronze rod and rings" },
];

const faqs = [
  {
    q: "Do linen curtains need to be ironed?",
    a: "Linen naturally creases, and a certain amount of relaxed texture is part of its charm. For a cleaner look, iron lightly while the fabric is slightly damp — this removes the worst creases without making the curtains look stiff. Many people prefer the relaxed, lived-in look of unironed linen, especially in a calm neutral room.",
  },
  {
    q: "How do I stop linen curtains from fading?",
    a: "Linen can fade in direct sunlight over time. To slow this down, choose a linen with a UV-resistant finish, or layer sheer panels with a heavier drape so the sheers take the brunt of the sun. Washing linen curtains in cool water and line-drying them also helps preserve the colour and fabric integrity.",
  },
  {
    q: "What length should linen curtains be?",
    a: "Always floor-to-ceiling — even if your windows are shorter. Hang the curtain rod as close to the ceiling as possible and let the curtains fall to the floor. This makes the room feel taller and the windows feel larger. A slight pool on the floor (2–5cm) adds a relaxed, editorial quality. Avoid curtains that stop at the window sill — they make the room feel smaller.",
  },
  {
    q: "Can I use linen curtains in a bedroom?",
    a: "Yes — but consider the lining. Unlined linen panels are beautiful in a living room where you want soft, filtered light. In a bedroom, a thermal or blackout lining gives you full light control while keeping the soft linen look on the outside. Oat or warm white linen with a blackout lining is one of the most popular combinations for a calm, restful bedroom.",
  },
];

export default function LCFormula() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0 pb-16">

      <div className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>The Formula</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>The Linen Curtain Checklist</h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-3">
          {checklist.map((c, i) => (
            <div key={i} className="flex items-start gap-4 border-b border-[#EDE6DA] pb-3 last:border-0">
              <div className="w-5 h-5 flex items-center justify-center rounded-full border border-[#C8BAA8] flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-[#C8BAA8] text-[10px]" />
              </div>
              <div>
                <p className="text-[#2C2825] text-sm" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{c.item}</p>
                <p className="text-[#B5A898] text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{c.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>FAQ</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Frequently Asked Questions</h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F5F1E8]/50 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-[#2C2825] text-sm leading-snug pr-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{faq.q}</span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-add-line text-[#8B8378] text-base transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Linen curtains are one of the simplest ways to make a room feel more considered.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          When hung correctly — high, wide, and with generous fullness — they transform even a modest window into something genuinely elegant.
          The light they filter, the texture they add, and the warmth they bring to a neutral room is hard to replicate with any other window treatment.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Choose natural linen, hang it high, and let it do the rest.
        </p>
      </div>

    </div>
  );
}
